import type { Point2D, RoofModule } from '../../gen/ejot/roofing/v1/types';

export type SiteGeometricRelation =
  | 'SEPARATE'
  | 'NEARBY'
  | 'TOUCHING_POINT'
  | 'TOUCHING_EDGE'
  | 'OVERLAPPING'
  | 'CONTAINED';

export type SiteEngineeringRelation =
  | 'INDEPENDENT'
  | 'ENGINEERING_REVIEW_REQUIRED'
  | 'HEIGHT_STEP';

export interface SitePairSummary {
  moduleAId: string;
  moduleBId: string;
  geometricRelation: SiteGeometricRelation;
  engineeringRelation: SiteEngineeringRelation;
  minimumSeparationMm: number;
  sharedEdgeLengthMm: number;
  deltaHeightMm: number;
  blockingReason?: string;
}

interface XY { x: number; y: number }

const EPS = 1e-6;

export function summarizeSitePairs(modules: readonly RoofModule[], nearbyThresholdMm = 5000): SitePairSummary[] {
  const usable = modules.filter((module) => (module.build_up?.plan?.outline?.points?.length ?? 0) >= 3);
  const summaries: SitePairSummary[] = [];
  for (let i = 0; i < usable.length; i += 1) {
    for (let j = i + 1; j < usable.length; j += 1) {
      const a = usable[i];
      const b = usable[j];
      if (!a || !b) continue;
      summaries.push(summarizePair(a, b, nearbyThresholdMm));
    }
  }
  return summaries;
}

export function modulePlanPoints(module: RoofModule): XY[] {
  return (module.build_up?.plan?.outline?.points ?? []).map((point) => ({
    x: point.x ?? 0,
    y: point.y ?? 0,
  }));
}

function summarizePair(a: RoofModule, b: RoofModule, nearbyThresholdMm: number): SitePairSummary {
  const pa = modulePlanPoints(a);
  const pb = modulePlanPoints(b);
  const sharedEdgeLengthMm = sharedEdgeLength(pa, pb);
  const minimumSeparationMm = minimumDistance(pa, pb);
  const aContainsB = polygonContains(pa, pb);
  const bContainsA = polygonContains(pb, pa);
  const overlapping = properlyIntersects(pa, pb);

  let geometricRelation: SiteGeometricRelation = 'SEPARATE';
  if (aContainsB || bContainsA) geometricRelation = 'CONTAINED';
  else if (overlapping) geometricRelation = 'OVERLAPPING';
  else if (sharedEdgeLengthMm > EPS) geometricRelation = 'TOUCHING_EDGE';
  else if (minimumSeparationMm <= EPS) geometricRelation = 'TOUCHING_POINT';
  else if (minimumSeparationMm <= nearbyThresholdMm) geometricRelation = 'NEARBY';

  const heightA = a.roof_height_above_ground?.millimetres ?? 0;
  const heightB = b.roof_height_above_ground?.millimetres ?? 0;
  const deltaHeightMm = Math.abs(heightA - heightB);
  let engineeringRelation: SiteEngineeringRelation = 'INDEPENDENT';
  let blockingReason: string | undefined;
  if (geometricRelation === 'OVERLAPPING' || geometricRelation === 'CONTAINED') {
    engineeringRelation = 'ENGINEERING_REVIEW_REQUIRED';
    blockingReason = 'Overlapping building footprints require modelling correction or an explicit engineering interpretation.';
  } else if (geometricRelation === 'TOUCHING_EDGE') {
    if (deltaHeightMm > EPS) {
      engineeringRelation = 'HEIGHT_STEP';
      blockingReason = 'Different-height attached roofs are not combined as one flat roof. A source-backed step/abutment rule is required.';
    } else {
      engineeringRelation = 'ENGINEERING_REVIEW_REQUIRED';
      blockingReason = 'Same-height attached roofs need an explicit aerodynamic continuity decision; geometric contact alone is not enough.';
    }
  } else if (geometricRelation === 'NEARBY') {
    engineeringRelation = 'ENGINEERING_REVIEW_REQUIRED';
    blockingReason = 'Nearby-building effects are reported but do not modify pressure until an approved source rule is available.';
  }

  return {
    moduleAId: a.id ?? '', moduleBId: b.id ?? '', geometricRelation, engineeringRelation,
    minimumSeparationMm, sharedEdgeLengthMm, deltaHeightMm, blockingReason,
  };
}

function sharedEdgeLength(a: XY[], b: XY[]): number {
  let total = 0;
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < b.length; j += 1) {
      const a1 = a[i];
      const a2 = a[(i + 1) % a.length];
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (!a1 || !a2 || !b1 || !b2) continue;
      total += collinearOverlapLength(a1, a2, b1, b2);
    }
  }
  return total;
}

function collinearOverlapLength(a1: XY, a2: XY, b1: XY, b2: XY): number {
  const dx = a2.x - a1.x;
  const dy = a2.y - a1.y;
  const length = Math.hypot(dx, dy);
  if (length <= EPS) return 0;
  const cross = (p: XY) => dx * (p.y - a1.y) - dy * (p.x - a1.x);
  if (Math.abs(cross(b1)) > EPS * Math.max(1, length) || Math.abs(cross(b2)) > EPS * Math.max(1, length)) return 0;
  const ux = dx / length;
  const uy = dy / length;
  const project = (p: XY) => (p.x - a1.x) * ux + (p.y - a1.y) * uy;
  const lo = Math.max(0, Math.min(project(b1), project(b2)));
  const hi = Math.min(length, Math.max(project(b1), project(b2)));
  return Math.max(0, hi - lo);
}

function minimumDistance(a: XY[], b: XY[]): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < b.length; j += 1) {
      const a1 = a[i]; const a2 = a[(i + 1) % a.length];
      const b1 = b[j]; const b2 = b[(j + 1) % b.length];
      if (!a1 || !a2 || !b1 || !b2) continue;
      if (segmentsIntersectInclusive(a1, a2, b1, b2)) return 0;
      best = Math.min(best,
        pointSegmentDistance(a1, b1, b2), pointSegmentDistance(a2, b1, b2),
        pointSegmentDistance(b1, a1, a2), pointSegmentDistance(b2, a1, a2));
    }
  }
  return best;
}

function properlyIntersects(a: XY[], b: XY[]): boolean {
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < b.length; j += 1) {
      const a1 = a[i];
      const a2 = a[(i + 1) % a.length];
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (!a1 || !a2 || !b1 || !b2) continue;
      if (segmentsProperlyIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

/**
 * Reports whether `inner` lies within `outer`.
 *
 * A single vertex is not evidence: two roofs laid side by side share the
 * corners of their common edge, and pointInPolygon counts a boundary point as
 * inside, so testing one vertex classifies neighbours as contained.
 * Containment requires every vertex inside or on the boundary and at least one
 * strictly inside, which a merely touching neighbour never satisfies.
 */
function polygonContains(outer: XY[], inner: XY[]): boolean {
  if (outer.length < 3 || inner.length < 3) return false;
  let strictlyInside = false;
  for (const point of inner) {
    if (!pointInPolygon(point, outer)) return false;
    if (!pointOnPolygonBoundary(point, outer)) strictlyInside = true;
  }
  return strictlyInside;
}

function pointOnPolygonBoundary(point: XY, ring: XY[]): boolean {
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const pi = ring[i]; const pj = ring[j];
    if (!pi || !pj) continue;
    if (pointSegmentDistance(point, pj, pi) <= EPS) return true;
  }
  return false;
}

function pointInPolygon(point: XY, ring: XY[]): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const pi = ring[i]; const pj = ring[j];
    if (!pi || !pj) continue;
    if (pointSegmentDistance(point, pj, pi) <= EPS) return true;
    const intersects = ((pi.y > point.y) !== (pj.y > point.y))
      && point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function segmentsProperlyIntersect(a: XY, b: XY, c: XY, d: XY): boolean {
  const o1 = orientation(a, b, c); const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a); const o4 = orientation(c, d, b);
  return o1 * o2 < -EPS && o3 * o4 < -EPS;
}

function segmentsIntersectInclusive(a: XY, b: XY, c: XY, d: XY): boolean {
  return segmentsProperlyIntersect(a, b, c, d)
    || pointSegmentDistance(a, c, d) <= EPS || pointSegmentDistance(b, c, d) <= EPS
    || pointSegmentDistance(c, a, b) <= EPS || pointSegmentDistance(d, a, b) <= EPS;
}

function orientation(a: XY, b: XY, c: XY): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function pointSegmentDistance(p: XY, a: XY, b: XY): number {
  const dx = b.x - a.x; const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq <= Number.EPSILON) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function clonePoint(point: Point2D): Point2D {
  return { ...point };
}